/**
 * The kitchen display.
 *
 * A screen on a wall, watched all through service. It is never tapped to
 * refresh — new orders have to appear on their own, so it polls.
 *
 * WHY POLLING AND NOT SOMETHING CLEVERER
 * --------------------------------------
 * The response is small, so polling is cheap whether the screen sits on the
 * same network as the till or reaches it over the internet. A websocket would
 * be fewer bytes and one more thing to reconnect when the connection drops —
 * which it does. Polling recovers from that by doing nothing at all.
 *
 * WHY IT DOES NOT SHOW AN ERROR WHEN A FETCH FAILS
 * ------------------------------------------------
 * A missed poll is not news; the next one is a few seconds away. What matters
 * is that the screen keeps showing the last known tickets rather than blanking
 * — a kitchen staring at an error message has lost the orders it was cooking.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { TicketCard } from './TicketCard.jsx';
import { arrivalsAmong, announce } from './arrivals.js';
import { unlockOnFirstGesture, whenUnlocked, canBeHeard, playNewOrderChime } from './chime.js';
import { urgency, LATE } from './waiting.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useAuth } from '../../stores/auth.js';

/** Fast enough that a ticket feels like it appears the moment it is sent. */
const POLL_MS = 4_000;

export default function KitchenPage() {
  const client = useQueryClient();

  // Cashiers can watch this screen but not work it; the backend refuses them.
  const readOnly = !useAuth((s) => s.can)('kitchen.update.kitchen');

  // The whole board at once — no station filter. A single-screen kitchen wants
  // to see everything it has to make, not one station's slice of it.
  const tickets = useQuery({
    queryKey: ['kitchen', 'tickets'],
    queryFn: () => api.get('/kitchen/tickets'),
    refetchInterval: POLL_MS,
    // Keep the last good list on screen while the next one is fetched, so the
    // board never flickers empty between polls.
    placeholderData: (previous) => previous,
    retry: false,
  });

  const advance = useMutation({
    mutationFn: ({ ticketId, action }) => api.post(`/kitchen/tickets/${ticketId}/${action}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['kitchen'] }),
  });

  /**
   * A new ticket makes a noise.
   *
   * WHY THE BOARD ALONE IS NOT ENOUGH
   * ---------------------------------
   * The board is on a wall and it is correct within seconds. But a kitchen at
   * seven in the evening is people facing a pass, not a screen, and an order
   * that arrives while nobody glances up waits until somebody does. The sound
   * is what a bell on a counter has always been for.
   *
   * The first draw is silent — it fills the board with tickets that are new to
   * the screen and old to the kitchen, and a burst of noise on every page load
   * is how a kitchen learns to ignore the sound entirely.
   */
  const seen = useRef(new Set());
  const firstLook = useRef(true);
  const [arrival, setArrival] = useState(null);
  const [silent, setSilent] = useState(!canBeHeard());

  // A browser will not play anything until somebody has touched the page.
  useEffect(() => {
    const stopWaiting = unlockOnFirstGesture();
    const stopWatching = whenUnlocked(() => setSilent(false));
    return () => {
      stopWaiting();
      stopWatching();
    };
  }, []);

  const list = tickets.data?.tickets ?? [];

  /**
   * What the kitchen actually has on, which is not always what fits on screen.
   *
   * The board draws the oldest sixty. Past that the heading used to count the
   * cards in front of it and say "60 orders to make" on a night with seventy —
   * a number that is wrong in the direction that matters, because it tells a
   * kitchen it is nearly through when it is not.
   */
  const waiting = tickets.data?.waiting ?? list.length;
  const offScreen = Math.max(0, waiting - list.length);

  useEffect(() => {
    if (tickets.isLoading) return undefined;

    const arrived = arrivalsAmong(list, seen.current, firstLook.current);
    firstLook.current = false;
    if (arrived.length === 0) return undefined;

    playNewOrderChime();
    setArrival(announce(arrived));

    // Long enough to be read from across a kitchen, short enough to be gone
    // before the next one lands.
    const clear = setTimeout(() => setArrival(null), 6000);
    return () => clearTimeout(clear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets.dataUpdatedAt, tickets.isLoading]);

  const late = list.filter((t) => urgency(t.elapsed_minutes) === LATE).length;

  if (tickets.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Loading orders" />
      </div>
    );
  }

  return (
    <div className="p-3">
      {/*
        Said as well as sounded. A kitchen is a loud room, somebody may have
        turned the screen's volume down, and a sound that has already happened
        cannot be checked — this can still be read a few seconds later by
        whoever looks up second.
      */}
      {arrival && (
        <div
          role="status"
          aria-live="polite"
          className="mb-3 flex items-center gap-3 rounded-lg bg-brand-400 px-4 py-3 font-semibold text-lg text-surface-2"
        >
          <BellIcon />
          {arrival}
        </div>
      )}

      {/*
        A browser makes no sound until somebody has touched the page, which on a
        screen that is switched on and left alone means never. Rather than fail
        silently — the exact thing this feature exists to prevent — it says so,
        and the first touch anywhere fixes it.
      */}
      {silent && (
        <p className="mb-3 rounded-lg bg-warn-bg px-4 py-2.5 text-sm text-warn">
          Tap anywhere on this screen once to turn the new-order sound on. The
          browser will not play it until somebody has.
        </p>
      )}

      <header className="mb-3">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="font-semibold text-lg text-ink">
            {waiting} order{waiting === 1 ? '' : 's'} to make
          </h1>

          {/* Said out loud, because a kitchen cannot see what is not drawn and
              would otherwise read a full board as the end of the queue. The
              rest arrive as this board clears. */}
          {offScreen > 0 && (
            <span className="rounded-full bg-warn-bg px-3 py-1 text-sm font-semibold text-warn">
              oldest {list.length} shown, {offScreen} more waiting
            </span>
          )}

          {readOnly && <span className="text-xs text-ink-3">View only</span>}

          {late > 0 && (
            <span className="rounded-full bg-bad-bg px-3 py-1 text-sm font-semibold text-bad">
              {late} running late
            </span>
          )}
        </div>

      </header>

      {list.length === 0 ? (
        <p className="py-24 text-center text-ink-3">
          Nothing waiting. New orders appear here on their own.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              readOnly={readOnly}
              // Only the ticket being advanced shows a spinner, not every card.
              busy={advance.isPending && advance.variables?.ticketId === ticket.id}
              onAdvance={(ticketId, action) => advance.mutate({ ticketId, action })}
            />
          ))}
        </div>
      )}

      {/* Deliberately quiet. A missed poll is not news, and a kitchen staring
          at an error message has lost the orders it was cooking. */}
      {tickets.isError && list.length > 0 && (
        <p className="mt-3 text-center text-xs text-ink-3">
          Not updating — check the branch computer. Showing the last known orders.
        </p>
      )}
    </div>
  );
}

/** A bell, drawn rather than fetched, so it cannot be the thing that is missing. */
function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
